import { useState, useEffect } from "react";
import { API } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Search,
  Plus,
  Minus,
  Trash2,
  ShoppingCart,
  CreditCard,
  Banknote,
  Receipt,
  Package,
  User,
  Wallet
} from "lucide-react";

const POSPage = () => {
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("especes");
  const [discount, setDiscount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [clientCredit, setClientCredit] = useState(0);
  const [useCredit, setUseCredit] = useState(false);

  const fetchProducts = async () => {
    try {
      const response = await API.get("/products", { params: { search, limit: 100 } });
      setProducts(response.data.products);
    } catch (error) {
      toast.error("Erreur lors du chargement des produits");
    }
  };

  const fetchClients = async () => {
    try {
      const response = await API.get("/clients", { params: { limit: 200 } });
      setClients(response.data.clients);
    } catch (error) {
      console.error("Error fetching clients");
    }
  };

  const fetchClientCredit = async (clientId) => {
    if (!clientId) {
      setClientCredit(0);
      setUseCredit(false);
      return;
    }
    try {
      const res = await API.get(`/clients/${clientId}/credit`);
      setClientCredit(res.data.solde || 0);
    } catch {
      setClientCredit(0);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchProducts();
  }, []);

  useEffect(() => {
    const timer = setTimeout(fetchProducts, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchClientCredit(selectedClient);
  }, [selectedClient]);

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.product_id === product.id);
      if (existing) {
        if (existing.quantite >= product.quantite_stock) {
          toast.error("Stock insuffisant");
          return prev;
        }
        return prev.map(item =>
          item.product_id === product.id
            ? { ...item, quantite: item.quantite + 1 }
            : item
        );
      } else {
        if (product.quantite_stock < 1) {
          toast.error("Produit en rupture de stock");
          return prev;
        }
        return [...prev, {
          product_id: product.id,
          nom: product.nom,
          reference: product.reference,
          prix_unitaire: product.prix_vente,
          quantite: 1,
          stock: product.quantite_stock,
          remise: 0
        }];
      }
    });
  };

  const updateQuantity = (productId, delta) => {
    setCart(prev => prev.map(item => {
      if (item.product_id === productId) {
        const newQty = item.quantite + delta;
        if (newQty < 1) return item;
        if (newQty > item.stock) {
          toast.error("Stock insuffisant");
          return item;
        }
        return { ...item, quantite: newQty };
      }
      return item;
    }));
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.product_id !== productId));
  };

  const updateItemDiscount = (productId, discount) => {
    setCart(prev => prev.map(item =>
      item.product_id === productId
        ? { ...item, remise: Math.min(100, Math.max(0, parseFloat(discount) || 0)) }
        : item
    ));
  };

  const calculateSubtotal = () => {
    return cart.reduce((total, item) => {
      return total + (item.prix_unitaire * item.quantite) * (1 - item.remise / 100);
    }, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    return subtotal * (1 - discount / 100);
  };

  const creditApplied = useCredit ? Math.min(clientCredit, calculateTotal()) : 0;
  const remainingToPay = calculateTotal() - creditApplied;

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    setLoading(true);
    try {
      const saleData = {
        client_id: selectedClient || null,
        articles: cart.map(item => ({
          product_id: item.product_id,
          quantite: item.quantite,
          prix_unitaire: item.prix_unitaire,
          remise: item.remise
        })),
        mode_paiement: creditApplied >= calculateTotal() ? "credit" : paymentMethod,
        credit_utilise: creditApplied,
        remise_globale: discount,
        notes: ""
      };

      if (paymentMethod === "stripe" && remainingToPay > 0) {
        const response = await API.post("/sales/stripe-checkout", saleData);
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
          return;
        }
      } else {
        const response = await API.post("/sales", saleData);
        if (response.data) {
          let msg = `Vente ${response.data.numero} enregistrée !`;
          if (creditApplied > 0) {
            msg += ` (${creditApplied.toFixed(2)}€ de crédit utilisé)`;
          }
          toast.success(msg);
        }
        setCart([]);
        setSelectedClient("");
        setDiscount(0);
        setUseCredit(false);
        setClientCredit(0);
        fetchProducts();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la vente");
    } finally {
      setLoading(false);
    }
  };

  const selectedClientObj = clients.find(c => c.id === selectedClient);

  return (
    <div data-testid="pos-page">
      <div className="mb-4">
        <h1 className="text-3xl font-bold tracking-tight">Caisse</h1>
        <p className="text-muted-foreground">Vente comptoir rapide</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 lg:h-[calc(100vh-10rem)]">
        {/* Products */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher un produit..."
              className="pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="pos-search-input"
            />
          </div>

          <div className="lg:flex-1 lg:overflow-y-auto lg:min-h-0">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 pb-4">
              {products.map((product) => (
                <Card
                  key={product.id}
                  className={`cursor-pointer transition-all hover:shadow-md active:scale-95 ${
                    product.quantite_stock < 1 ? "opacity-50" : ""
                  }`}
                  onClick={() => addToCart(product)}
                  data-testid={`pos-product-${product.id}`}
                >
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex items-start justify-between mb-2">
                      <Package className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground" />
                      <Badge variant={product.quantite_stock < 1 ? "destructive" : "secondary"} className="text-xs">
                        {product.quantite_stock}
                      </Badge>
                    </div>
                    <p className="font-medium text-xs sm:text-sm line-clamp-2 mb-1">{product.nom}</p>
                    <p className="text-[10px] sm:text-xs text-muted-foreground font-mono mb-1">{product.reference}</p>
                    <p className="text-base sm:text-lg font-bold text-primary">{product.prix_vente?.toFixed(2)}€</p>
                  </CardContent>
                </Card>
              ))}
              {products.length === 0 && (
                <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
                  <Package className="h-12 w-12 text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">Aucun produit trouvé</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cart */}
        <Card className="w-full lg:w-96 lg:flex lg:flex-col lg:min-h-0" data-testid="pos-cart">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Panier
              {cart.length > 0 && (
                <Badge variant="secondary" className="ml-auto">
                  {cart.reduce((sum, item) => sum + item.quantite, 0)} article(s)
                </Badge>
              )}
            </CardTitle>
          </CardHeader>

          <CardContent className="lg:flex-1 lg:flex lg:flex-col lg:min-h-0">
            <div className="mb-4">
              <Select value={selectedClient || "none"} onValueChange={(v) => setSelectedClient(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="pos-client-select">
                  <User className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Client (optionnel)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Pas de client</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.prenom} {client.nom}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Credit info */}
            {selectedClient && clientCredit > 0 && (
              <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="pos-credit-info">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wallet className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm font-medium text-emerald-800">
                      Crédit : {clientCredit.toFixed(2)}€
                    </span>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className="text-xs text-emerald-700">Utiliser</span>
                    <input
                      type="checkbox"
                      checked={useCredit}
                      onChange={(e) => setUseCredit(e.target.checked)}
                      className="h-4 w-4 rounded border-emerald-300 text-emerald-600 accent-emerald-600"
                      data-testid="pos-use-credit-toggle"
                    />
                  </label>
                </div>
              </div>
            )}

            <div className="lg:flex-1 lg:overflow-y-auto lg:min-h-0">
              {cart.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <ShoppingCart className="h-12 w-12 text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">Panier vide</p>
                  <p className="text-sm text-muted-foreground">Cliquez sur un produit pour l'ajouter</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {cart.map((item) => (
                    <div key={item.product_id} className="p-3 rounded-lg bg-secondary/50" data-testid={`cart-item-${item.product_id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{item.nom}</p>
                          <p className="text-xs text-muted-foreground font-mono">{item.reference}</p>
                        </div>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive shrink-0" onClick={() => removeFromCart(item.product_id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-input bg-background hover:bg-accent active:bg-accent"
                            onClick={() => updateQuantity(item.product_id, -1)}
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <input
                            key={`qty-${item.product_id}-${item.quantite}`}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            defaultValue={item.quantite}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/[^0-9]/g, '');
                              if (raw === '') return;
                              const val = parseInt(raw);
                              if (!isNaN(val) && val >= 1) {
                                const qty = Math.min(val, item.stock);
                                if (qty < val) {
                                  toast.error(`Stock max: ${item.stock}`);
                                  e.target.value = String(qty);
                                }
                                setCart(prev => prev.map(i => i.product_id === item.product_id ? { ...i, quantite: qty } : i));
                              }
                            }}
                            onBlur={(e) => {
                              e.target.value = String(item.quantite);
                            }}
                            className="w-16 h-9 text-center font-mono text-sm border border-input rounded-md bg-background [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            data-testid={`qty-input-${item.product_id}`}
                          />
                          <button
                            type="button"
                            className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-input bg-background hover:bg-accent active:bg-accent"
                            onClick={() => updateQuantity(item.product_id, 1)}
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground">Remise</span>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={item.remise || ""}
                            placeholder="0"
                            onChange={(e) => updateItemDiscount(item.product_id, e.target.value)}
                            className="w-14 h-7 text-xs text-right"
                            data-testid={`item-discount-${item.product_id}`}
                          />
                          <span className="text-xs text-muted-foreground">%</span>
                        </div>
                        <p className="font-mono font-medium ml-auto">
                          {(item.prix_unitaire * item.quantite * (1 - item.remise / 100)).toFixed(2)}€
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <div className="pt-4 mt-4 border-t space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm">Remise globale %</span>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={discount}
                    onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                    className="w-20 text-right"
                  />
                </div>

                <Separator />

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Sous-total</span>
                    <span className="font-mono">{calculateSubtotal().toFixed(2)}€</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Remise ({discount}%)</span>
                      <span className="font-mono">-{(calculateSubtotal() * discount / 100).toFixed(2)}€</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-medium">
                    <span>Total TTC</span>
                    <span className="font-mono">{calculateTotal().toFixed(2)}€</span>
                  </div>
                  {creditApplied > 0 && (
                    <>
                      <div className="flex justify-between text-sm text-emerald-600 font-medium">
                        <span className="flex items-center gap-1"><Wallet className="h-3.5 w-3.5" /> Crédit appliqué</span>
                        <span className="font-mono">-{creditApplied.toFixed(2)}€</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold">
                        <span>Reste à payer</span>
                        <span className="font-mono text-primary">{remainingToPay.toFixed(2)}€</span>
                      </div>
                    </>
                  )}
                  {!creditApplied && (
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total TTC</span>
                      <span className="font-mono text-primary">{calculateTotal().toFixed(2)}€</span>
                    </div>
                  )}
                </div>

                {/* Payment methods - hidden when fully paid by credit */}
                {remainingToPay > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    <Button
                      variant={paymentMethod === "especes" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setPaymentMethod("especes")}
                      className="flex flex-col gap-1 h-auto py-2"
                    >
                      <Banknote className="h-4 w-4" />
                      <span className="text-xs">Espèces</span>
                    </Button>
                    <Button
                      variant={paymentMethod === "cb" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setPaymentMethod("cb")}
                      className="flex flex-col gap-1 h-auto py-2"
                    >
                      <CreditCard className="h-4 w-4" />
                      <span className="text-xs">CB</span>
                    </Button>
                    <Button
                      variant={paymentMethod === "stripe" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setPaymentMethod("stripe")}
                      className="flex flex-col gap-1 h-auto py-2"
                    >
                      <Receipt className="h-4 w-4" />
                      <span className="text-xs">Stripe</span>
                    </Button>
                  </div>
                )}

                <Button
                  className="w-full h-12 text-lg"
                  onClick={handleCheckout}
                  disabled={loading || cart.length === 0}
                  data-testid="pos-checkout-btn"
                >
                  {loading ? (
                    "Traitement..."
                  ) : creditApplied >= calculateTotal() ? (
                    <>
                      <Wallet className="h-5 w-5 mr-2" />
                      Payer {calculateTotal().toFixed(2)}€ par crédit
                    </>
                  ) : paymentMethod === "stripe" && remainingToPay > 0 ? (
                    <>Payer {remainingToPay.toFixed(2)}€ via Stripe{creditApplied > 0 ? ` + ${creditApplied.toFixed(2)}€ crédit` : ""}</>
                  ) : (
                    <>Encaisser {remainingToPay > 0 ? remainingToPay.toFixed(2) : calculateTotal().toFixed(2)}€{creditApplied > 0 ? ` + ${creditApplied.toFixed(2)}€ crédit` : ""}</>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default POSPage;
